import { useEffect, useState } from 'react'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, LabelList,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { fmtNum, fmtInt, fmtDate, fmtDayShort } from '../i18n/format'

// Página de impressão do relatório de KPIs (/?report=1&mine=&from=&to=&preset=&lang=).
// Renderizada pelo Gotenberg (Chromium headless) via GET /api/report/pdf; quando a
// montagem termina, seta window.__REPORT_READY__ = true (waitForExpression do
// Gotenberg). Fundo branco SEMPRE (ignora o dark mode), sem WebSocket: só fetch.
// O Gotenberg não tem localStorage: o idioma vem do param ?lang= (fallback pt-BR).

const nf0 = (v) => fmtInt(v ?? 0)
const nf1 = (v) => fmtNum(v ?? 0, { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const fmtDT = (v) => (v ? fmtDate(v, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
// Dia/mês no formato do idioma do relatório (03/12 é 3 de dezembro em pt-BR e 12 de
// março em en).
const fmtDay = (d) => fmtDayShort(d)
const mmss = (secs) => {
  if (!secs || secs <= 0) return '—'
  const m = Math.floor(secs / 60)
  const s = Math.round(secs % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const GMG_COLORS = { productive: '#17b26a', delay: '#92400e', standby: '#f79009', non_productive: '#dd2590', downtime: '#f04438' }
const GMG_ORDER = ['productive', 'delay', 'standby', 'non_productive', 'downtime']

function Section({ title, children }) {
  return (
    <section className="rp-section" style={{ breakInside: 'avoid' }}>
      <h2>{title}</h2>
      {children}
    </section>
  )
}

export default function ReportPrint() {
  const { t } = useTranslation()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [authFailed, setAuthFailed] = useState(false)

  const params = new URLSearchParams(window.location.search)
  const mine = params.get('mine')
  // O idioma do ?lang= já é aplicado na inicialização do i18n (src/i18n/index.js), antes
  // do primeiro render: aqui não há troca de idioma no meio da montagem para o Gotenberg
  // capturar meia página em português.

  useEffect(() => {
    const qs = new URLSearchParams()
    for (const p of ['mine', 'from', 'to', 'preset']) {
      const v = params.get(p)
      if (v) qs.set(p, v)
    }
    fetch(`/api/kpis?${qs.toString()}`)
      .then((r) => {
        // Falha de AUTENTICAÇÃO não pode virar um PDF de 200 com uma caixa de erro
        // vermelha dentro: sem sinalizar o pronto, o Gotenberg estoura o timeout e o
        // backend responde 502, que é uma falha visível. Ver reporttoken.go.
        if (r.status === 401 || r.status === 403) {
          setAuthFailed(true)
          return Promise.reject(new Error('sem autorização para o relatório'))
        }
        return r.ok ? r.json() : r.json().then((b) => Promise.reject(new Error(b.error || `HTTP ${r.status}`)))
      })
      .then(setData)
      .catch((e) => setError(e.message || String(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sinaliza pro Gotenberg que a página terminou de montar (double rAF: espera o
  // commit + a pintura dos gráficos). Também no erro de NEGÓCIO, senão o Gotenberg
  // trava; no erro de autenticação, não, de propósito.
  useEffect(() => {
    if (authFailed) return undefined
    if (!data && !error) return undefined
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => { window.__REPORT_READY__ = true })
    })
    return () => cancelAnimationFrame(id)
  }, [data, error])

  const scopeLabel = !mine || mine === 'all' ? t('common:scope.all_mines') : mine === 'none' ? t('common:scope.no_mine') : `${t('common:scope.mine')} ${mine}`

  const css = `
    .report { background: #fff; color: #111827; max-width: 780px; margin: 0 auto; padding: 24px 16px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; }
    .report h1, .report h2 { font-family: Georgia, 'Times New Roman', serif; color: #0f172a; }
    .report h1 { font-size: 22px; margin: 0; }
    .report h2 { font-size: 15px; margin: 0 0 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
    .rp-header { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #0d9488;
      padding-bottom: 12px; margin-bottom: 16px; }
    .rp-header img { height: 24px; }
    .rp-sub { color: #6b7280; font-size: 12px; margin-top: 2px; }
    .rp-section { margin-bottom: 18px; }
    .report table { width: 100%; border-collapse: collapse; font-size: 11px; }
    .report th, .report td { border: 1px solid #e5e7eb; padding: 4px 8px; text-align: left; }
    .report th { background: #f8fafc; font-weight: 600; color: #334155; }
    .report td.num, .report th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .rp-error { border: 1px solid #f04438; background: #fef3f2; color: #b42318;
      border-radius: 6px; padding: 12px; font-size: 12px; }
    .rp-print { position: fixed; top: 12px; right: 12px; background: #0d9488; color: #fff;
      border: none; border-radius: 6px; padding: 8px 14px; font-size: 13px; cursor: pointer; }
    @media print { .rp-print { display: none; } .rp-section { break-inside: avoid; } }
    @page { size: A4; margin: 12mm; }
  `

  if (error) {
    return (
      <div className="report">
        <style>{css}</style>
        <div className="rp-error">
          <strong>{t('report:load_error')}</strong>
          <div>{error}</div>
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="report">
        <style>{css}</style>
        <div className="rp-sub">{t('common:states.loading')}</div>
      </div>
    )
  }

  const prod = data.production || {}
  const gmg = data.gmg || {}
  const cyc = data.cycles || {}
  const drill = data.drilling || {}
  const stops = gmg.stops || {}
  const byDay = prod.byDay || []
  const byShift = prod.byShift || []
  const pareto = stops.pareto || []
  const byOp = cyc.byOperator || []
  const stepAvgs = cyc.stepAvgs || {}

  const shiftLabel = (code) => (code ? t(`production:shift.${code}`, { defaultValue: String(code) }) : '—')

  const kpiRows = [
    [t('report:kpi_tons'), `${nf0(prod.totalTon)} ${t('common:units.tons')}`],
    [t('common:units.bcm'), nf0(prod.totalBcm)],
    [t('report:kpi_cycles'), nf0(prod.cycles)],
    [t('report:kpi_loads'), nf0(prod.loads)],
    [t('report:kpi_df_pct'), nf1(gmg.df)],
    [t('report:kpi_uf_pct'), nf1(gmg.uf)],
    [t('report:kpi_ro_pct'), nf1(gmg.ro)],
    [t('report:kpi_mtbf_h'), nf1(gmg.mtbfHours)],
    [t('report:kpi_mttr_h'), nf1(gmg.mttrHours)],
    [t('report:kpi_meters_drilled'), `${nf0(drill.meters)} ${t('common:units.meters')}`],
  ]

  // byBucket vem em MINUTOS; esta tabela é toda em HORAS, então ÷60.
  const buckets = stops.byBucket || {}
  const gmgRow = {
    name: 'GMG',
    productive: (gmg.productiveSecs || 0) / 3600,
    delay: (buckets.delay || 0) / 60,
    standby: (buckets.standby || 0) / 60,
    non_productive: (buckets.non_productive || 0) / 60,
    downtime: (buckets.downtime || 0) / 60,
  }
  const gmgTotal = gmgRow.productive + gmgRow.delay + gmgRow.standby + gmgRow.non_productive + gmgRow.downtime
  const hourLabel = (v) => (v > gmgTotal * 0.04 ? `${nf1(v)}${t('common:units.hours')}` : '')

  const totalStopMin = pareto.reduce((s, p) => s + (p.minutes || 0), 0)
  let acc = 0
  const paretoRows = pareto.map((p) => {
    acc += p.minutes || 0
    return { ...p, cumPct: totalStopMin > 0 ? (acc / totalStopMin) * 100 : 0 }
  })

  return (
    <div className="report">
      <style>{css}</style>
      {!navigator.webdriver && (
        <button type="button" className="rp-print" onClick={() => window.print()}>{t('common:actions.print')}</button>
      )}

      <header className="rp-header">
        <img src="/carcosa-logo.svg" alt="Carcosa Secrets" className="h-6" />
        <div>
          <h1>{t('report:title')}</h1>
          <div className="rp-sub">
            {scopeLabel} · {t('report:period', { from: fmtDT(data.window?.from), to: fmtDT(data.window?.to) })}
          </div>
        </div>
      </header>

      <Section title={t('report:summary')}>
        <table>
          <tbody>
            {[0, 5].map((off) => (
              <tr key={off}>
                {kpiRows.slice(off, off + 5).map(([label, value]) => (
                  <td key={label}>
                    <div style={{ color: '#6b7280', fontSize: 10 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {byDay.length > 0 && (
        <Section title={t('report:production_by_day')}>
          <BarChart width={700} height={220} data={byDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(v) => nf0(v)} />
            <Bar dataKey="ton" name={t('report:series_tons')} fill="#0d9488" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </Section>
      )}

      {byDay.length > 0 && (
        <Section title={t('report:cumulative_plan_vs_actual')}>
          <LineChart width={700} height={220} data={byDay} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: '#6b7280' }} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(v) => nf0(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="cumTon" name={t('report:series_actual')} stroke="#0d9488" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="cumPlan" name={t('report:series_plan')} stroke="#1570ef" strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </Section>
      )}

      {gmgTotal > 0 && (
        <Section title={t('report:gmg_time_model')}>
          <BarChart width={700} height={110} data={[gmgRow]} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={(v) => nf0(v)} />
            <YAxis type="category" dataKey="name" hide />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {GMG_ORDER.map((k) => (
              <Bar key={k} dataKey={k} name={t(`production:gmg.${k}`)} stackId="gmg" isAnimationActive={false}
                fill={GMG_COLORS[k]} stroke="#ffffff" strokeWidth={2}>
                <LabelList dataKey={k} position="center" fill="#ffffff" fontSize={10} formatter={hourLabel} />
              </Bar>
            ))}
          </BarChart>
        </Section>
      )}

      {paretoRows.length > 0 && (
        <Section title={t('report:stops_pareto')}>
          <table>
            <thead>
              <tr>
                <th>{t('report:reason')}</th>
                <th className="num">{t('report:minutes')}</th>
                <th className="num">{t('report:occurrences')}</th>
                <th className="num">{t('report:cum_pct')}</th>
              </tr>
            </thead>
            <tbody>
              {paretoRows.map((p) => (
                <tr key={p.reason}>
                  <td>{p.reason}</td>
                  <td className="num">{nf0(p.minutes)}</td>
                  <td className="num">{nf0(p.count)}</td>
                  <td className="num">{nf1(p.cumPct)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {byShift.length > 0 && (
        <Section title={t('report:production_by_shift')}>
          <table>
            <thead>
              <tr>
                <th>{t('report:day')}</th>
                <th>{t('common:entities.shift')}</th>
                <th className="num">{t('report:tons')}</th>
                <th className="num">{t('report:plan_t')}</th>
                <th className="num">%</th>
              </tr>
            </thead>
            <tbody>
              {byShift.map((s) => (
                <tr key={`${s.day}-${s.shift}`}>
                  <td>{fmtDay(s.day)}</td>
                  <td>{shiftLabel(s.shift)}</td>
                  <td className="num">{nf0(s.ton)}</td>
                  <td className="num">{nf0(s.planTon)}</td>
                  <td className="num">{s.planTon > 0 ? `${nf0((s.ton / s.planTon) * 100)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {(cyc.count || 0) > 0 && (
        <Section title={t('report:cycle_times_avg')}>
          <table>
            <thead>
              <tr>
                <th>{t('report:step_queue')}</th>
                <th>{t('report:step_load')}</th>
                <th>{t('report:step_travel')}</th>
                <th>{t('report:step_unload')}</th>
                <th>{t('report:full_cycle')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{mmss(stepAvgs.queue)}</td>
                <td>{mmss(stepAvgs.load)}</td>
                <td>{mmss(stepAvgs.travel)}</td>
                <td>{mmss(stepAvgs.unload)}</td>
                <td>{mmss(cyc.avgSecs)}</td>
              </tr>
            </tbody>
          </table>
        </Section>
      )}

      {byOp.length > 0 && (
        <Section title={t('report:by_operator')}>
          <table>
            <thead>
              <tr>
                <th>{t('common:entities.operator')}</th>
                <th className="num">{t('common:entities.cycles')}</th>
                <th className="num">{t('report:tons')}</th>
                <th className="num">{t('report:avg_cycle')}</th>
              </tr>
            </thead>
            <tbody>
              {byOp.map((o) => (
                <tr key={o.operator}>
                  <td>{o.operator}</td>
                  <td className="num">{nf0(o.cycles)}</td>
                  <td className="num">{nf0(o.ton)}</td>
                  <td className="num">{mmss(o.avgSecs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {(drill.holes || 0) > 0 && (
        <Section title={t('report:drilling')}>
          <table>
            <thead>
              <tr>
                <th className="num">{t('common:entities.holes')}</th>
                <th className="num">{t('report:meters')}</th>
                <th className="num">{t('common:units.meters_per_hour')}</th>
                <th className="num">{t('report:avg_depth_per_hole')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="num">{nf0(drill.holes)}</td>
                <td className="num">{nf1(drill.meters)}</td>
                <td className="num">{nf1(drill.metersPerHour)}</td>
                <td className="num">{nf1(drill.avgDepthPerHole)}</td>
              </tr>
            </tbody>
          </table>
        </Section>
      )}
    </div>
  )
}
